import type { ProdantixClient } from 'ProdantixSdk/core/client';
import { CHECKLIST_TASK_DONE, GUIDE_COMPLETED, GUIDE_DISMISSED, GUIDE_SHOWN, GUIDE_STEP_MISSED, GUIDE_STEP_VIEWED, type Guide, type GuideMemory, guideKey, isGuideDue, taskFor, taskKey, tasksDone } from 'ProdantixSdk/core/guides';
import { isDue, type Survey, type SurveyMemory, settledKey } from 'ProdantixSdk/core/surveys';
import type { StorageAdapter } from 'ProdantixSdk/core/types';
import type { Checklist } from 'ProdantixSdk/web/engage/guides';
import 'ProdantixSdk/web/engage/global';
import type { GuideKit, SurveyKit } from 'ProdantixSdk/web/engage/global';
import { engageLanguage } from 'ProdantixSdk/web/engage/strings';

export type EngageClient = Pick<ProdantixClient, 'captureGuide' | 'captureSurveyDismissed' | 'captureSurveySent' | 'captureSurveyShown' | 'getGuides' | 'getSurveys' | 'onCapture'>;

export interface EngageOptions {
  edgeHost: string;
  guides: boolean;
  namespace: string;
  onError: (error: unknown) => void;
  storage: StorageAdapter;
  surveys: boolean;
}

interface EngageMemory {
  guides: GuideMemory;
  surveys: SurveyMemory;
}

const OWN_EVENTS = /^\$(survey|guide|checklist)_/;

export const emptyMemory = (): EngageMemory => ({ guides: { done: [], settled: [] }, surveys: { settled: [] } });

export function readMemory(storage: StorageAdapter, key: string): EngageMemory {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return emptyMemory();
    const parsed = JSON.parse(raw) as Partial<EngageMemory>;
    return {
      guides: { done: parsed.guides?.done ?? [], lastShownAt: parsed.guides?.lastShownAt, settled: parsed.guides?.settled ?? [] },
      surveys: { lastShownAt: parsed.surveys?.lastShownAt, settled: parsed.surveys?.settled ?? [] },
    };
  } catch {
    return emptyMemory();
  }
}

function loadKit<T>(win: Window, src: string, read: () => T | undefined): Promise<T | null> {
  const ready = read();
  if (ready) return Promise.resolve(ready);
  return new Promise(resolve => {
    const script = win.document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve(read() ?? null);
    script.onerror = () => resolve(null);
    win.document.head.append(script);
  });
}

export function installEngage(client: EngageClient, win: Window, options: EngageOptions): () => void {
  const key = `pdx_engage_${options.namespace}`;
  const memory = readMemory(options.storage, key);
  const save = (): void => {
    try {
      options.storage.setItem(key, JSON.stringify(memory));
    } catch (error) {
      options.onError(error);
    }
  };
  const language = engageLanguage(win.document.documentElement.lang || win.navigator.language);
  let surveys: Survey[] = [];
  let guides: Guide[] = [];
  let surveyOpen = false;
  let tourOpen = false;
  const checklists = new Map<string, Checklist>();
  const surveyKit = (): Promise<SurveyKit | null> => loadKit(win, `${options.edgeHost}/surveys.js`, () => win.prodantixSurveys);
  const guideKit = (): Promise<GuideKit | null> => loadKit(win, `${options.edgeHost}/guides.js`, () => win.prodantixGuides);

  const showSurveys = async (event?: string): Promise<void> => {
    if (surveyOpen) return;
    const survey = surveys.find(candidate => isDue(candidate, memory.surveys, win.location.href, Date.now(), event));
    if (!survey) return;
    surveyOpen = true;
    const kit = await surveyKit();
    if (!kit) {
      surveyOpen = false;
      return;
    }
    const settle = (): void => {
      memory.surveys.settled = [...memory.surveys.settled.filter(entry => entry !== settledKey(survey)), settledKey(survey)];
      save();
      surveyOpen = false;
    };
    kit.showSurvey(win.document, survey, language, {
      onDismissed: () => {
        client.captureSurveyDismissed(survey);
        settle();
      },
      onSent: answers => {
        client.captureSurveySent(survey, answers);
        settle();
      },
      onShown: () => {
        memory.surveys.lastShownAt = Date.now();
        save();
        client.captureSurveyShown(survey);
      },
    });
  };

  const settleGuide = (guide: Guide): void => {
    memory.guides.settled = [...memory.guides.settled.filter(entry => entry !== guideKey(guide)), guideKey(guide)];
    save();
  };

  const showGuides = async (event?: string): Promise<void> => {
    const due = guides.filter(guide => !checklists.has(guide.id) && isGuideDue(guide, memory.guides, win.location.href, Date.now(), event));
    const lists = due.filter(guide => guide.kind === 'checklist');
    const tour = tourOpen ? undefined : due.find(guide => guide.kind !== 'checklist');
    if (lists.length === 0 && !tour) return;
    if (tour) tourOpen = true;
    const kit = await guideKit();
    if (!kit) {
      tourOpen = false;
      return;
    }
    for (const guide of lists) {
      if (checklists.has(guide.id)) continue;
      checklists.set(
        guide.id,
        kit.showChecklist(win.document, guide, language, tasksDone(guide, memory.guides), {
          onDismissed: () => {
            client.captureGuide(GUIDE_DISMISSED, guide);
            settleGuide(guide);
            checklists.delete(guide.id);
          },
          onShown: () => client.captureGuide(GUIDE_SHOWN, guide),
        }),
      );
    }
    if (!tour) return;
    const show = tour.kind === 'tooltip' ? kit.showTooltip : kit.showTour;
    show(win.document, tour, language, {
      onCompleted: () => {
        client.captureGuide(GUIDE_COMPLETED, tour);
        settleGuide(tour);
        tourOpen = false;
      },
      onDismissed: () => {
        client.captureGuide(GUIDE_DISMISSED, tour);
        settleGuide(tour);
        tourOpen = false;
      },
      onMissed: stepId => client.captureGuide(GUIDE_STEP_MISSED, tour, stepId),
      onShown: () => {
        memory.guides.lastShownAt = Date.now();
        save();
        client.captureGuide(GUIDE_SHOWN, tour);
      },
      onStep: stepId => client.captureGuide(GUIDE_STEP_VIEWED, tour, stepId),
    });
  };

  const completeTasks = (eventName: string): void => {
    for (const guide of guides) {
      const task = taskFor(guide, eventName);
      if (!task || memory.guides.done.includes(taskKey(guide, task.id)) || memory.guides.settled.includes(guideKey(guide))) continue;
      memory.guides.done = [...memory.guides.done, taskKey(guide, task.id)];
      client.captureGuide(CHECKLIST_TASK_DONE, guide, task.id);
      checklists.get(guide.id)?.markDone(task.id);
      if (tasksDone(guide, memory.guides).length === guide.steps.length) {
        client.captureGuide(GUIDE_COMPLETED, guide);
        settleGuide(guide);
        checklists.get(guide.id)?.close();
        checklists.delete(guide.id);
      }
      save();
    }
  };

  const check = (event?: string): void => {
    if (options.surveys) void showSurveys(event).catch(options.onError);
    if (options.guides) void showGuides(event).catch(options.onError);
  };

  const stop = client.onCapture(eventName => {
    if (OWN_EVENTS.test(eventName)) return;
    completeTasks(eventName);
    check(eventName);
  });
  const onNavigate = (): void => check();
  win.addEventListener('popstate', onNavigate);

  const start = async (): Promise<void> => {
    const failed = <T>(error: unknown): T[] => {
      options.onError(error);
      return [];
    };
    const [readSurveys, readGuides] = await Promise.all([options.surveys ? client.getSurveys(win.document.documentElement.lang || win.navigator.language).catch(failed<Survey>) : [], options.guides ? client.getGuides().catch(failed<Guide>) : []]);
    surveys = readSurveys;
    guides = readGuides;
    check();
  };
  if (win.document.readyState === 'loading') win.document.addEventListener('DOMContentLoaded', () => void start(), { once: true });
  else void start();

  return () => {
    stop();
    win.removeEventListener('popstate', onNavigate);
    for (const list of checklists.values()) list.close();
    checklists.clear();
  };
}
